import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardTitle,
  CountPill,
  Dialog,
  EmptyState,
  Input,
  LoadingState,
  SearchInput,
  Select,
  StatusDot,
  Table,
  Td,
  Textarea,
  Th,
  useToast,
} from '@up2d8/ui';
import tokens from '@up2d8/ui/tokens.json';
import { AdminPage, Card as AdminCard, CardHead, CardPad, PageHead } from '../admin-kit';
import { ActiveBrand, useCssVars } from './active-brand';
import { KitPreview } from './kit-preview';

/**
 * Het designsysteem: elke basiscomponent en elke token, in de twee sets van het product.
 *
 * - **Beheer**: de onderdelen van de admin-kit (`modules/admin-kit`), zoals het beheerportaal en
 *   dit platform ze gebruiken - sectie 5 van de basis-spec van het beheer.
 * - **App**: de componenten van `packages/ui` in de AA-set die de `app`-zone verplicht gebruikt
 *   (ADR-0018, /docs/design/a11y.md).
 * - **Tokens**: de AA-varianten van het beheer (sectie 2 van de basis-spec) en de tokens van de
 *   style guide.
 *
 * Onder elkaar en niet in tabs: de toegankelijkheidstest (`e2e/accessibility.spec.ts`) meet elk
 * raakvlak van de app-zone op deze pagina, en dat kan alleen als alles er tegelijk staat. Wie een
 * component wijzigt, ziet hier meteen of het nog klopt. Dit vervangt Storybook in Stap 0; zie
 * /docs/design/deviations.md. De voorbeeldteksten zijn met opzet Nederlands en vast: het is een
 * pagina voor ontwikkelaars, de rand eromheen volgt de taal.
 */
export function DesignSystemPage() {
  const { t } = useTranslation();

  return (
    <AdminPage>
      <PageHead title={t('nav.designSystem')} sub={t('beheer.platform.design.sub')} />

      <h2 className="a-sect-title">{t('beheer.merk.design.title')}</h2>
      <ActiveBrand />

      <h2 className="a-sect-title">{t('beheer.platform.design.admin')}</h2>
      <KitPreview />

      <h2 className="a-sect-title">{t('beheer.platform.design.app')}</h2>
      <ZonePreview />

      <h2 className="a-sect-title">{t('beheer.platform.design.tokens')}</h2>
      <AdminTokens />
      <TokenTables />
    </AdminPage>
  );
}

/**
 * Sectie 2 van de basis-spec: de AA-varianten van het beheer, met hun gebruik.
 *
 * Zonder vaste waarden (MRK-3): de waarde komt uit de browser (`getComputedStyle`), zodat de tabel
 * het actieve merk toont. Een overgetikte hexcode zou bij een ander platformmerk liegen.
 */
const ADMIN_TOKENS: [string, string][] = [
  ['--a-primary', 'primaire knop, link, actieve tab, focus (wit erop 4,5:1 of meer)'],
  ['--a-primary-hover', 'hover; tekst op de primaire tint'],
  ['--a-orange', 'tellers die aandacht vragen (wit erop 4,5:1 of meer)'],
  ['--a-orange-text', 'oranje tekst op de oranje tint'],
  ['--a-success-text', 'groene tekst op de groene tint'],
  ['--a-danger', 'gevaarlijke knop'],
  ['--a-danger-text', 'rode tekst op de rode tint'],
  ['--a-muted', 'secundaire tekst (7,6:1)'],
  ['--a-subtle', 'hulptekst, kolomkoppen (4,8:1)'],
  ['--a-line', 'lijnen'],
  ['--a-line-soft', 'lijnen'],
  ['--a-field', 'rand van invoervelden en omlijnde knoppen'],
];
const ADMIN_TOKEN_NAMES = ADMIN_TOKENS.map(([name]) => name);

function AdminTokens() {
  const { t } = useTranslation();
  const values = useCssVars(ADMIN_TOKEN_NAMES);
  return (
    <AdminCard>
      <CardHead title={t('beheer.platform.design.adminTokens')} />
      <CardPad>
        <p className="a-muted">{t('beheer.platform.design.adminTokensIntro')}</p>
      </CardPad>
      <div className="a-table-wrap">
        <table className="a-table">
          <thead>
            <tr>
              <th>Token</th>
              <th>Waarde</th>
              <th>Gebruik</th>
            </tr>
          </thead>
          <tbody>
            {ADMIN_TOKENS.map(([name, gebruik]) => (
              <tr key={name}>
                <td>
                  <span className="a-inline" style={{ gap: 10 }}>
                    <span
                      aria-hidden="true"
                      className="a-swatch"
                      style={{ width: 24, height: 24, borderRadius: 6, background: `var(${name})` }}
                    />
                    <span className="a-mono">{name}</span>
                  </span>
                </td>
                <td className="a-mono">{values[name] || '—'}</td>
                <td className="a-muted">{gebruik}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminCard>
  );
}

/** Eén kolom met alle componenten van `packages/ui`, in de AA-set van de app-zone. */
function ZonePreview() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const toast = useToast();

  return (
    <div data-zone="app" className="rounded-lg border border-border bg-background p-card">
      <p className="mb-element font-heading text-title-md font-semibold text-text-primary">
        AA-set — app-zone (verplicht)
      </p>

      <div className="flex flex-col gap-element">
        <Card compact>
          <p className="mb-2 text-caption uppercase tracking-[0.08em] text-text-muted">Knoppen</p>
          <div className="flex flex-wrap items-center gap-inline">
            <Button>Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="urgent">Urgent</Button>
            <Button variant="icon" label="Zoeken">
              <SearchGlyph />
            </Button>
            <Button loading>Bezig</Button>
            <Button disabled>Uit</Button>
          </div>
        </Card>

        <Card compact>
          <p className="mb-2 text-caption uppercase tracking-[0.08em] text-text-muted">Invoer</p>
          <div className="flex flex-col gap-element">
            <Input label="Personeelsnummer" hint="Zoals op je badge" defaultValue="10428" />
            <Input label="Met fout" error="Dit nummer bestaat niet." defaultValue="99999" />
            <SearchInput label="Zoeken" placeholder="Zoek een medewerker" />
            <Select label="Ploeg" defaultValue="nacht">
              <option value="vroeg">Vroege ploeg</option>
              <option value="laat">Late ploeg</option>
              <option value="nacht">Nachtploeg</option>
            </Select>
            <Textarea label="Bericht" placeholder="Wat wil je melden?" />
          </div>
        </Card>

        <Card compact>
          <p className="mb-2 text-caption uppercase tracking-[0.08em] text-text-muted">
            Badges, avatars en status
          </p>
          <div className="flex flex-wrap items-center gap-inline">
            <Badge tone="primary">Nieuws</Badge>
            <Badge tone="dark-blue">HR</Badge>
            <Badge tone="cyan">Locatie</Badge>
            <Badge tone="orange">Dringend</Badge>
            <Badge tone="success">Bevestigd</Badge>
            <Badge tone="warning">Wacht</Badge>
            <Badge tone="danger">Veiligheid</Badge>
            <CountPill value={7} />
            <CountPill value={128} />
            <Avatar name="Ana Popescu" />
            <Avatar name="Ingelogde Gebruiker" self />
            <StatusDot tone="success" label="Actief" />
            <StatusDot tone="neutral" label="Uit dienst" />
          </div>
        </Card>

        <Card compact>
          <p className="mb-2 text-caption uppercase tracking-[0.08em] text-text-muted">Tabel</p>
          <Table>
            <thead>
              <tr>
                <Th>Naam</Th>
                <Th>Ploeg</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td>Ana Popescu</Td>
                <Td>Nacht</Td>
              </tr>
              <tr>
                <Td>Marek Kowalski</Td>
                <Td>Vroeg</Td>
              </tr>
            </tbody>
          </Table>
        </Card>

        <Card compact>
          <p className="mb-2 text-caption uppercase tracking-[0.08em] text-text-muted">
            Toestanden en overlays
          </p>
          <EmptyState
            title="Nog geen berichten"
            description="Zodra HR iets publiceert, staat het hier."
          />
          <LoadingState rows={2} />
          <div className="mt-element flex gap-inline">
            <Button variant="secondary" onClick={() => setDialogOpen(true)}>
              Dialoog
            </Button>
            <Button
              variant="secondary"
              onClick={() => toast.show({ title: 'Opgeslagen', tone: 'success' })}
            >
              Toast
            </Button>
          </div>
          <Dialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            title="Bericht intrekken?"
            description="De medewerkers zien het bericht daarna niet meer in hun feed."
            footer={
              <>
                <Button variant="secondary" onClick={() => setDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button variant="urgent" onClick={() => setDialogOpen(false)}>
                  Intrekken
                </Button>
              </>
            }
          />
        </Card>
      </div>
    </div>
  );
}

function TokenTables() {
  return (
    <Card>
      <CardTitle>Tokens</CardTitle>
      <p className="mt-2 text-content text-text-secondary">
        Rechtstreeks uit <code>packages/ui/tokens.json</code>, de machineleesbare kopie van de style
        guide. De contrastcheck in CI leest hetzelfde bestand.
      </p>

      <div className="mt-card grid gap-section md:grid-cols-2">
        <Swatches title="Kleuren (guide)" entries={Object.entries(tokens.color)} />
        <Swatches title="Kleuren (AA-set)" entries={Object.entries(tokens.colorAa)} />
      </div>

      <div className="mt-card grid gap-section md:grid-cols-3">
        <ValueList title="Radius" entries={Object.entries(tokens.radius)} />
        <ValueList title="Shadow" entries={Object.entries(tokens.shadow)} />
        <ValueList title="Spacing" entries={Object.entries(tokens.spacing)} />
      </div>
    </Card>
  );
}

function Swatches({ title, entries }: { title: string; entries: [string, string][] }) {
  return (
    <div>
      <p className="mb-2 text-caption uppercase tracking-[0.08em] text-text-muted">{title}</p>
      <ul className="flex flex-col gap-1">
        {entries.map(([name, value]) => (
          <li key={name} className="flex items-center gap-inline text-small">
            <span
              aria-hidden
              className="h-6 w-6 shrink-0 rounded-sm border border-border"
              style={{ background: value }}
            />
            <code className="text-text-primary">--{name}</code>
            <span className="ml-auto text-text-muted">{value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ValueList({ title, entries }: { title: string; entries: [string, string | number][] }) {
  return (
    <div>
      <p className="mb-2 text-caption uppercase tracking-[0.08em] text-text-muted">{title}</p>
      <ul className="flex flex-col gap-1 text-small">
        {entries.map(([name, value]) => (
          <li key={name} className="flex justify-between gap-inline">
            <code className="text-text-primary">--{name}</code>
            <span className="text-text-muted">{String(value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SearchGlyph() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}
